import { IPhoneNumber } from "../types/interfaces";

export const identityCardTypes = ["Passport", "National ID", "Driver’s License"];
export const dateFormat = 'YYYY-MM-DD';
export const getPhoneNumbersArr = (initPhoneNumbers: any): string[] => {
    const phonesArr: string[] | null = [];
    initPhoneNumbers?.forEach((item: IPhoneNumber) => {
        if (item.phoneNumber != null) {
            phonesArr.push(item.phoneNumber);
        }
    });
    return phonesArr;
}

export const KYC_STATUS = {
  PENDING: 1,
  CONFIRMED: 2,
  REJECTED: 3,
  PRE_APPROVE: 4
} as const;

export const KYC_STATUS_LEGACY = {
  NEW: 'New',
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
  PREAPPROVED: 'Preapproved'
} as const;

export const KYC_STATUS_DISPLAY = {
  [KYC_STATUS.PENDING]: 'Under Review',
  [KYC_STATUS.CONFIRMED]: 'Verified',
  [KYC_STATUS.REJECTED]: 'Needs Update',
  [KYC_STATUS.PRE_APPROVE]: 'Verified'
} as const;

export const KYC_STATUS_TO_LEGACY = {
  [KYC_STATUS.PENDING]: KYC_STATUS_LEGACY.PENDING,
  [KYC_STATUS.CONFIRMED]: KYC_STATUS_LEGACY.CONFIRMED,
  [KYC_STATUS.REJECTED]: KYC_STATUS_LEGACY.REJECTED,
  [KYC_STATUS.PRE_APPROVE]: KYC_STATUS_LEGACY.PREAPPROVED
} as const;

export const LEGACY_TO_KYC_STATUS = {
  [KYC_STATUS_LEGACY.NEW]: KYC_STATUS.PENDING,
  [KYC_STATUS_LEGACY.PENDING]: KYC_STATUS.PENDING,
  [KYC_STATUS_LEGACY.CONFIRMED]: KYC_STATUS.CONFIRMED,
  [KYC_STATUS_LEGACY.REJECTED]: KYC_STATUS.REJECTED,
  [KYC_STATUS_LEGACY.PREAPPROVED]: KYC_STATUS.PRE_APPROVE
} as const;

export type KycStatusNumeric = typeof KYC_STATUS[keyof typeof KYC_STATUS];
export type KycStatusLegacy = typeof KYC_STATUS_LEGACY[keyof typeof KYC_STATUS_LEGACY];
export type KycStatusValue = KycStatusNumeric | KycStatusLegacy;

export const normalizeKycStatus = (status: KycStatusValue | null | undefined): KycStatusNumeric | null => {
  if (status === null || status === undefined) {
    return null;
  }

  if (typeof status === 'number') {
    return status as KycStatusNumeric;
  }

  return LEGACY_TO_KYC_STATUS[status as KycStatusLegacy] || null;
};

export const isKycStatusValid = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return false;
  }

  return normalizedStatus === KYC_STATUS.CONFIRMED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusConfirmedOrPreapproved = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.CONFIRMED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusPreapproved = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusRejected = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.REJECTED;
};

export const isKycStatusPending = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.PENDING;
};

export const isKycStatusConfirmed = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.CONFIRMED;
};

export const getKycStatusDisplayName = (status: KycStatusValue | null | undefined): string => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return 'Unknown';
  }
  return KYC_STATUS_DISPLAY[normalizedStatus] || 'Unknown';
};

export const getKycStatusLegacyValue = (status: KycStatusValue | null | undefined): string | null => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return null;
  }
  return KYC_STATUS_TO_LEGACY[normalizedStatus] || null;
};

export const doesKycStatusRequireAction = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.REJECTED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};